import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, map, catchError, of, shareReplay, finalize } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { User, AuthResponse, LoginRequest, RegisterRequest } from '../models/user.model';
import { AREA_LANDING, NO_ACCESS_URL } from '../core/permissions';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private readonly TOKEN_KEY = 'foodciti_token';
  private readonly USER_KEY = 'foodciti_user';

  constructor() {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const userStr = localStorage.getItem(this.USER_KEY);
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next({ ...user, permissions: [] });
      } catch (error) {
        // If stored user JSON is corrupted, clear auth state without forcing navigation.
        // This avoids redirecting users away from the public landing page on app load.
        this.logout(false);
      }
    }
  }

  register(data: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/register`, data).pipe(
      tap((response:any) => {
        if (response.success) {
          this.setAuthData(response.data.user, response.data.token);
        }
      })
    );
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/login`, credentials).pipe(
      tap((response:any) => {
        if (response.success) {
          this.setAuthData(response.data.user, response.data.token);
        }
      })
    );
  }

  logout(redirect = true): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem('restaurantId'); // Clear restaurant ID on logout
    this.currentUserSubject.next(null);

    if (redirect) {
      this.router.navigate(['/']);
    }
  }

  private setAuthData(user: User, token: string): void {
    this.accessVerified = true;
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
    
    // Handle restaurant ID based on user role
    this.handleRestaurantIdOnLogin(user);
  }

  private handleRestaurantIdOnLogin(user: User): void {
    // If super admin, clear any existing restaurant ID
    // They must select a restaurant manually from the dropdown
    if (user.role === 'super_admin') {
      localStorage.removeItem('restaurantId');
    } else {
      // For restaurant owner, manager, staff - save restaurant ID from user data
      if (user.restaurant && (user.restaurant as any)._id) {
        const restaurantId = (user.restaurant as any)._id;
        localStorage.setItem('restaurantId', restaurantId);
      }
    }
  }

  /**
   * Re-reads the signed-in user from the server so a change a super admin
   * made (such as linking this login to a business) shows up without signing
   * out. Keeps the token, and only emits when the role, home restaurant or
   * business actually changed.
   */
  private accessVerified = false;
  private permissionRequest?: Observable<boolean>;
  private permissionOutlet = '';
  refreshPermissions(): Observable<boolean> {
    if (!this.getToken()) return of(false);
    const restaurantId = localStorage.getItem('restaurantId') || '';
    if (this.permissionRequest && this.permissionOutlet === restaurantId) return this.permissionRequest;
    if (this.permissionOutlet !== restaurantId) this.accessVerified = false;
    this.permissionOutlet = restaurantId;
    const query = restaurantId ? `?restaurantId=${encodeURIComponent(restaurantId)}` : '';
    this.permissionRequest = this.http.get<any>(`${environment.apiUrl}/auth/me${query}`).pipe(
      map(response => {
        if (restaurantId !== (localStorage.getItem('restaurantId') || '')) return false;
        const fresh = response?.data?.user;
        if (!fresh) throw new Error('Unable to verify access');
        this.accessVerified = true;
        const current = this.currentUserSubject.value;
        const merged = { ...current, ...fresh, permissions: Array.isArray(fresh.permissions) ? fresh.permissions : [] };
        if (JSON.stringify(current) !== JSON.stringify(merged)) {
          localStorage.setItem(this.USER_KEY, JSON.stringify(merged));
          this.currentUserSubject.next(merged);
        }
        return true;
      }),
      catchError(() => {
        this.accessVerified = false;
        const user = this.currentUserSubject.value;
        if (user) this.currentUserSubject.next({ ...user, permissions: [] });
        return of(false);
      }),
      finalize(() => { this.permissionRequest = undefined; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.permissionRequest;
  }

  refreshCurrentUser(): void { this.refreshPermissions().subscribe(); }

  /** Email a 6-digit password-reset code (the reply never says whether the email has an account). */
  requestPasswordReset(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/auth/forgot-password`, { email });
  }

  resetPassword(email: string, code: string, newPassword: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/auth/reset-password`, { email, code, newPassword });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.http.put(`${environment.apiUrl}/auth/change-password`, { currentPassword, newPassword });
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /** Areas of the panel the signed-in login may use. */
  permissions(): string[] {
    const user = this.currentUserSubject.value;
    return Array.isArray(user?.permissions) ? user!.permissions! : [];
  }

  /** Super admins may use every area; everyone else needs the key. */
  can(key: string): boolean {
    const user = this.currentUserSubject.value;
    if (!user || !this.accessVerified) return false;
    if (user.role === 'super_admin') return true;
    return this.permissions().includes(key);
  }

  canAny(keys: string[]): boolean {
    return keys.some((key) => this.can(key));
  }

  /**
   * The first page this login may open, used by the guards instead of a fixed
   * `/admin/dashboard` — a staff login without the Dashboard area would
   * otherwise be redirected to a page its own guard refuses.
   */
  firstAllowedUrl(): string {
    const user = this.currentUserSubject.value;
    if (!user) return '/login';
    if (!this.accessVerified) return NO_ACCESS_URL;
    if (user.role === 'super_admin') return '/admin/dashboard';
    const landing = AREA_LANDING.find((area) => this.can(area.key));
    return landing ? landing.url : NO_ACCESS_URL;
  }
}
